import { CodeMapSet, CodeMapSetService, CodeMapType } from "@app/codemaps";
import { ClientInsuranceModel, InsuranceService } from "@app/insurance";
import { MemberSetModel, MemberSetService } from "@app/members";
import { Project, ProjectService } from "@app/projects";
import { SpecialtyOverrides } from "@app/projects/create/interfaces";
import EditProjectOverrideMemberSet from "@app/projects/editProjectOverrideMemberSet";
import { EditPerspective, ProjectOverrideMemberSetType, TimeDistanceCalculatorType } from "@app/projects/enumerators";
import { EditOptionsModule } from "@app/projects/literals";
import { ProviderSet, ProviderSetService } from "@app/provider-sets";
import { ResourceService, ResourceType } from "@app/resources";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import { QESAlert, QESButton } from "@app/shared/qes-standards";
import { preventDefaultMouseEvent } from "@app/shared/utility";
import { AccessCriteriaGranularityEnum, AccuracySpecialtyDefinition, AccuracyTemplateCollectionMetadata, AccuracyTemplateService, TemplateCollectionMetadata, TemplateMetadata, TemplateService, TemplateSpecialtyGroup } from "@app/templates";
import { Roles, UserContext, UserContextProps, withUserContext } from "@app/user";
import { difference, isEqual, sortBy } from "lodash";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Link } from "react-router-dom";
import { MemberSetTypeEnum } from "@app/members/enumerations";
import { LoadingSpinner } from "@app/shared";
import { Modal } from "@app/shared/Modals";
import { ProjectExclusionType } from "../enumerators";
import { Details } from "./EditDetails";
import { EditDisplayOptions } from "./EditDisplayOptions";
import { MemberOptions } from "./EditMemberOptions";
import ProviderOptions from "./EditProvidersOptions";
import { TemplateOptions } from "./EditTemplateOptions";
import { EditTemplateOptionsHelper, TemplateInfo } from "./EditTemplateOptionsHelper";
import Features from "@app/feature-flags/features";
import NetworkTaxonomyContentPanel from "@app/network-taxonomies/NetworkTaxonomyContentPanel";
import { NetworkTaxonomyData } from "@app/network-taxonomies/networkTaxonomy";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { cloneProjectThingsToResetIfZipProject, cloneThingsToResetIfZipProject } from "../helpers";
import ZipProjectService from "../zipProjectService";
import { ResourceTagModel } from "@app/tags/import/types";
import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import { ManageResourceTagDisplayModel, Perspectives } from "@app/tags/types";

type ProjectOptionsProps = {
  disabled: boolean;
  onChange: (projectOptionsDirty: boolean) => void;
  projectId: number;
  onRunProject?: () => Promise<void>;
  onRunProjectSuccess?: () => Promise<void>;
  onSubmitSuccess?: () => Promise<void>;
  sidePanelId?: string;
} & RouteComponentProps<unknown> & FeatureFlagContextProps & UserContextProps & FlyoutContextOutgoingProps;

export interface ProjectOptionsState {
  accuracyInsurancesIsLoading: boolean;
  accuracyTemplateCollections: AccuracyTemplateCollectionMetadata[];
  accuracySpecialtyDefinitions: AccuracySpecialtyDefinition[];
  allAccessCodeMapSets: CodeMapSet[];
  allMemberPopulations: MemberSetModel[];
  authorizedInsurances: ClientInsuranceModel[];
  disableUnsupportedOptions: boolean;
  error: string;
  excludeDeactivatedFacilityImpact: boolean;
  excludeDeactivatedProviderImpact: boolean;
  excludeDeactivatedFacilityNetwork: boolean;
  excludeDeactivatedProviderNetwork: boolean;
  excludeInvalidNppesNpiFacilityImpact: boolean;
  excludeInvalidNppesNpiFacilityNetwork: boolean;
  excludeInvalidNppesNpiProviderImpact: boolean;
  excludeInvalidNppesNpiProviderNetwork: boolean;
  excludeMissingNpiFacilityImpact: boolean;
  excludeMissingNpiFacilityNetwork: boolean;
  excludeMissingNpiProviderImpact: boolean;
  excludeMissingNpiProviderNetwork: boolean;
  excludeMedicareOptOutImpact: boolean;
  excludeOIGExclusionImpact: boolean;
  excludePrecludedImpact: boolean;
  excludeMedicareOptOutNetwork: boolean;
  excludeOIGExclusionNetwork: boolean;
  excludePrecludedNetwork: boolean;
  isNetworkTaxonomyValid: boolean;
  isZipProject: boolean;
  marketProviderSets: ProviderSet[];
  providerSets: ProviderSet[];
  project: Project;
  projectAccessCodeMapSets: CodeMapSet[];
  projectMemberPopulations: MemberSetModel[];
  resourceTags: ManageResourceTagDisplayModel[];
  selectedInsurances: ClientInsuranceModel[];
  selectedInsuranceUid: string;
  sortedSpecialtyGroups: TemplateSpecialtyGroup[];
  specialtyCodeMapSets: CodeMapSet[];
  submitClicked: boolean;
  templateAccessGranularity: AccessCriteriaGranularityEnum;
  templateCollections: TemplateCollectionMetadata[];
  timeDistanceCalculatorTypes: TimeDistanceCalculatorType[];
  activeEditModule: EditOptionsModule;
  alternateMemberships: MemberSetModel[];
  disableMemberBindingTable: boolean;
  specialtyGroups: TemplateSpecialtyGroup[];
  specialtyOverrides: SpecialtyOverrides[];
  specialtyToQdpCodeMapSets: CodeMapSet[];

  useAccuracy: boolean;
  useAdequacy: boolean;

  hideCMSApplicationCreditOption: boolean;
  hideCareJourneyScores: boolean;
  hideClosestProviderReportOption: boolean;
  hideCreateStandards: boolean;
  hideEnterpriseComplianceDashboard: boolean;
  hideMotiveAppropriatenessScore: boolean;
  hideStrictTimeDistance: boolean;
  hideUseInMarketAnalysis: boolean;
  includeInEnterpriseComplianceDashboard: boolean;
  showNetworkSpecialtyCodeMap: boolean;
  showAffiliationsInsights: boolean;
  displayOnNetworkAccordion: boolean;
  warningForDisableDescription: string;
  warningForDisableOption: UpdateResourceKeys;
}

type UpdateResourceKeys = keyof Project;

class EditProject extends React.Component<ProjectOptionsProps, ProjectOptionsState> {
  private readonly accuracyTemplateService = new AccuracyTemplateService();
  private readonly codeMapSetService = new CodeMapSetService();
  private readonly insuranceService = new InsuranceService();
  private readonly memberSetService = new MemberSetService();
  private initialProjectState: {
    excludeDeactivatedFacilityImpact: boolean,
    excludeDeactivatedFacilityNetwork: boolean,
    excludeDeactivatedProviderImpact: boolean,
    excludeDeactivatedProviderNetwork: boolean,
    excludeInvalidNppesNpiFacilityImpact: boolean,
    excludeInvalidNppesNpiFacilityNetwork: boolean,
    excludeInvalidNppesNpiProviderImpact: boolean,
    excludeInvalidNppesNpiProviderNetwork: boolean,
    excludeMissingNpiFacilityImpact: boolean,
    excludeMissingNpiFacilityNetwork: boolean,
    excludeMissingNpiProviderImpact: boolean,
    excludeMissingNpiProviderNetwork: boolean,
    excludeMedicareOptOutImpact: boolean,
    excludeOIGExclusionImpact: boolean,
    excludePrecludedImpact: boolean,
    excludeMedicareOptOutNetwork: boolean,
    excludeOIGExclusionNetwork: boolean,
    excludePrecludedNetwork: boolean,
    project: Project,
    showAffiliationsInsights: boolean,
    displayOnNetworkAccordion: boolean,
    timeDistanceCalculatorTypes?: TimeDistanceCalculatorType[],
  };

  private readonly projectService = new ProjectService();
  private readonly providerSetService = new ProviderSetService();
  private readonly resourceService = new ResourceService();
  private readonly templateService = new TemplateService();
  private readonly templateHelper = new EditTemplateOptionsHelper();
  private readonly sortOrder: Array<keyof TemplateSpecialtyGroup> = ["displayOrder"];
  private readonly zipProjectService = new ZipProjectService();
  private readonly resourceTaggingService = new ResourceTaggingService();

  private ecdCheckboxRef = React.createRef<HTMLInputElement>();
  private projectNames: string[] = [];
  private currentProjectName: string;

  constructor(props: ProjectOptionsProps) {
    super(props);

    this.state = {
      accuracyInsurancesIsLoading: false,
      accuracySpecialtyDefinitions: [],
      accuracyTemplateCollections: [],
      activeEditModule: "Details",
      allAccessCodeMapSets: [],
      allMemberPopulations: [],
      alternateMemberships: [],
      authorizedInsurances: [],
      disableMemberBindingTable: true,
      disableUnsupportedOptions: false,
      displayOnNetworkAccordion: false,
      error: null,
      excludeDeactivatedFacilityImpact: null,
      excludeDeactivatedFacilityNetwork: null,
      excludeDeactivatedProviderImpact: null,
      excludeDeactivatedProviderNetwork: null,
      excludeInvalidNppesNpiFacilityImpact: null,
      excludeInvalidNppesNpiFacilityNetwork: null,
      excludeInvalidNppesNpiProviderImpact: null,
      excludeInvalidNppesNpiProviderNetwork: null,
      excludeMedicareOptOutImpact: null,
      excludeMedicareOptOutNetwork: null,
      excludeMissingNpiFacilityImpact: null,
      excludeMissingNpiFacilityNetwork: null,
      excludeMissingNpiProviderImpact: null,
      excludeMissingNpiProviderNetwork: null,
      excludeOIGExclusionImpact: null,
      excludeOIGExclusionNetwork: null,
      excludePrecludedImpact: null,
      excludePrecludedNetwork: null,
      hideCMSApplicationCreditOption: true,
      hideCareJourneyScores: true,
      hideClosestProviderReportOption: false,
      hideCreateStandards: true,
      hideEnterpriseComplianceDashboard: true,
      hideMotiveAppropriatenessScore: true,
      hideStrictTimeDistance: false,
      hideUseInMarketAnalysis: true,
      includeInEnterpriseComplianceDashboard: false,
      isNetworkTaxonomyValid: true,
      isZipProject: false,
      marketProviderSets: [],
      project: null,
      projectAccessCodeMapSets: [],
      projectMemberPopulations: null,
      providerSets: [],
      resourceTags: [],
      selectedInsuranceUid: "",
      selectedInsurances: [],
      showAffiliationsInsights: false,
      showNetworkSpecialtyCodeMap: false,
      sortedSpecialtyGroups: null,
      specialtyCodeMapSets: [],
      specialtyGroups: [],
      specialtyOverrides: [],
      specialtyToQdpCodeMapSets: [],
      submitClicked: false,
      templateAccessGranularity: AccessCriteriaGranularityEnum.Unknown,
      templateCollections: [],
      timeDistanceCalculatorTypes: [],

      useAccuracy: false,
      useAdequacy: false,

      warningForDisableDescription: "",
      warningForDisableOption: null,
    };
  }

  public async componentDidMount() {
    let haveAdequacy = false;
    let initialTaxonomyIsValid = this.state.isNetworkTaxonomyValid;

    try {

      if (this.props.projectId) {
        const templateCollections = await this.templateService.getUnarchivedTemplateCollections();
        const project: Project = await this.projectService.getById(this.props.projectId);

        const userIsResourceAdmin = this.props.user.roles.includes(Roles.CLIENT_ADMIN);
        const resourceTags = userIsResourceAdmin ? await this.resourceTaggingService.getClientProjectTagInfo(Perspectives.All) : [];

        if (!project.networkTaxonomy) {
          project.networkTaxonomy = null;
          initialTaxonomyIsValid = !this.props.enabledFeatureFlags.has(Features.REQUIRED_TAXONOMY);
        }
        else {
          if (project.networkTaxonomy.fileTypeId === undefined)
            project.networkTaxonomy.fileTypeId = null;
        }

        const excludeDeactivatedFacilityImpact =
          project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.DeactivatedFacility);
        const excludeDeactivatedFacilityNetwork =
          project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.DeactivatedFacility);
        const excludeDeactivatedProviderImpact =
          project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.DeactivatedProvider);
        const excludeDeactivatedProviderNetwork =
          project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.DeactivatedProvider);
        const excludeInvalidNppesNpiFacilityImpact =
          project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.InvalidNppesNpiFacility);
        const excludeInvalidNppesNpiFacilityNetwork =
          project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.InvalidNppesNpiFacility);
        const excludeInvalidNppesNpiProviderImpact =
          project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.InvalidNppesNpiProvider);
        const excludeInvalidNppesNpiProviderNetwork =
          project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.InvalidNppesNpiProvider);
        const excludeMissingNpiFacilityImpact =
          project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.MissingNpiFacility);
        const excludeMissingNpiFacilityNetwork =
          project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.MissingNpiFacility);
        const excludeMissingNpiProviderImpact =
          project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.MissingNpiProvider);
        const excludeMissingNpiProviderNetwork =
          project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.MissingNpiProvider);
        const excludeMedicareOptOutNetwork =
          project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.MedicareOptOut);
        const excludeOIGExclusionNetwork =
          project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.OIGExclusion);
        const excludePrecludedNetwork =
          project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.Precluded);
        const excludeMedicareOptOutImpact =
          project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.MedicareOptOut);
        const excludeOIGExclusionImpact =
          project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.OIGExclusion);
        const excludePrecludedImpact =
          project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.Precluded);
        const showAffiliationsInsights = project.showAffiliationsInsights;
        const displayOnNetworkAccordion = project.displayOnNetworkAccordion;
        if (!project.resource?.isShared) {

          //change undefined properties to null so dirty checks work when an object is populated then unpopulated
          if (project.prospectProviderSetId === undefined) {
            project.prospectProviderSetId = null;
          }
          if (project.marketProviderSetId === undefined) {
            project.marketProviderSetId = null;
          }
          if (project.accessCodeMapSetId === undefined) {
            project.accessCodeMapSetId = null;
          }

          const accuracyTemplateCollections = await this.accuracyTemplateService.getTemplateCollections();
          const accuracySpecialtyDefinitions = await this.accuracyTemplateService.getSpecialtyDefinitions();
          const allMemberPopulations = (project.adequacyTemplateCollectionId && project.adequacyTemplateCorrelationId) ? await this.memberSetService.getActive(true, false) : [];
          let allAccessCodeMapSets = await this.codeMapSetService.get(CodeMapType.CountyClassMap);
          allAccessCodeMapSets =
            allAccessCodeMapSets.concat(await this.codeMapSetService.get(CodeMapType.ZipCodeClassMap));

          const specialtyCodeMapSets = await this.codeMapSetService.get(CodeMapType.SpecialtyCodeMap);
          const specialtyToQdpCodeMapSets = await this.codeMapSetService.get(CodeMapType.SpecialtyToQDPCodeMap);

          const marketProviderSets = await this.providerSetService.getMarketProviderSets();
          const providerSets = await this.providerSetService.getProviderSets();

          let templateInfo: TemplateInfo = null;
          let sortedSpecialtyGroups: TemplateSpecialtyGroup[] = null;
          let specialtyGroups: TemplateSpecialtyGroup[] = null;
          let sortedAuthorizedInsurances: ClientInsuranceModel[] = [];
          let selectedInsurances: ClientInsuranceModel[] = [];
          let showMotiveAppropriatenessScore = false;
          let isZipTemplate = false;

          if (project.accuracyTemplateCollectionId && project.accuracyTemplateCorrelationId) {
            this.setState((prevState) => ({
              ...prevState,
              useAccuracy: true,
            }));
          }

          if (project.adequacyTemplateCollectionId && project.adequacyTemplateCorrelationId) {
            this.setState((prevState) => ({
              ...prevState,
              useAdequacy: true,
            }));

            templateInfo = await this.templateHelper.getTemplateInfo(
              this.initialProjectState,
              allAccessCodeMapSets,
              allMemberPopulations,
              templateCollections,
              project.adequacyTemplateCollectionId,
              project.adequacyTemplateCorrelationId,
              project.templateId);

            specialtyGroups =
              await this.templateService.getTemplateSpecialtyGroups(project.adequacyTemplateCollectionId, project.templateId);

            sortedSpecialtyGroups =
              templateInfo.specialtyGroups && sortBy(templateInfo.specialtyGroups, this.sortOrder);

            showMotiveAppropriatenessScore = templateInfo.motiveCrosswalkCodeMapSetId !== undefined && templateInfo.motiveCrosswalkCodeMapSetId != null;

            haveAdequacy = true;
            isZipTemplate = this.IsProjectUsingZipTemplate(templateCollections, project);

            if (isZipTemplate)
              project.excludeInVolumeActions = true;
          }

          //this is an accuracy-only project, get specialty groups
          if (project.accuracySpecialtyDefinitionId) {
            specialtyGroups = await this.getAccuracySpecialtyGroups(project.accuracySpecialtyDefinitionId);
            sortedSpecialtyGroups = await this.getAccuracySpecialtyGroups(project.accuracySpecialtyDefinitionId);
          }

          const hasCmsApplicationCredits: boolean = sortedSpecialtyGroups.some((specialtyGroup) => specialtyGroup.hasCMSApplicationCreditPercentages);

          const showCareJourneyScores = this.props.enabledFeatureFlags.has(Features.ENABLE_CARE_JOURNEY_SCORES);
          const showUseInMarketAnalysis = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
          const showEnterpriseComplianceDashboard = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
          const showNetworkSpecialtyCodeMap = this.props.enabledFeatureFlags.has(Features.NETWORK_BENCHMARKING) && this.props.user.roles.includes(Roles.COMPETITIVE_INSIGHTS_USER);
          const hideStrictTimeDistance = this.props.enabledFeatureFlags.has(Features.HIDE_STRICT_TIME_DISTANCE);
          const hideStandardSettingCreation = !(this.props.enabledFeatureFlags.has(Features.STANDARD_SETTING_CREATION) && this.props.user.roles.includes(Roles.STANDARDS_CREATOR_USER));

          //this project includes accuracy. Determine selected insurances, if any
          if (project && project.accuracyTemplateCollectionId) {
            let error = this.state.error;

            try {
              this.setState({ accuracyInsurancesIsLoading: true });
              const authorizedInsurances = await this.insuranceService.getAuthorizedInsurancesByClientId();
              sortedAuthorizedInsurances = authorizedInsurances && sortBy(authorizedInsurances, "planName");
            }
            catch {
              error = "Unable to load available accuracy insurance plans at this time.";
            }

            if (this.state.accuracyInsurancesIsLoading || error) {
              this.setState({
                accuracyInsurancesIsLoading: false,
                error: error
              });
            }

            if (project.inspectedInsurancesList) {
              selectedInsurances = sortedAuthorizedInsurances.filter((ins) => (project.inspectedInsurancesList.findIndex((lookup) => lookup.uid === ins.uid) > -1));
            }
          }

          project.editProjectOverrideMemberSets = haveAdequacy ? this.getActiveMembershipOverrides(project) : this.getFreshMembershipOverrides(project);

          this.initialProjectState = {
            displayOnNetworkAccordion,
            excludeDeactivatedFacilityImpact,
            excludeDeactivatedFacilityNetwork,
            excludeDeactivatedProviderImpact,
            excludeDeactivatedProviderNetwork,
            excludeInvalidNppesNpiFacilityImpact,
            excludeInvalidNppesNpiFacilityNetwork,
            excludeInvalidNppesNpiProviderImpact,
            excludeInvalidNppesNpiProviderNetwork,
            excludeMedicareOptOutImpact,
            excludeMedicareOptOutNetwork,
            excludeMissingNpiFacilityImpact,
            excludeMissingNpiFacilityNetwork,
            excludeMissingNpiProviderImpact,
            excludeMissingNpiProviderNetwork,
            excludeOIGExclusionImpact,
            excludeOIGExclusionNetwork,
            excludePrecludedImpact,
            excludePrecludedNetwork,
            project,
            showAffiliationsInsights,
            timeDistanceCalculatorTypes: templateInfo ? templateInfo.timeDistanceCalculatorTypes : [TimeDistanceCalculatorType.Quest],
          };

          this.currentProjectName = project.resource?.name;

          this.setState({
            accuracySpecialtyDefinitions,
            accuracyTemplateCollections,
            allAccessCodeMapSets,
            allMemberPopulations,
            authorizedInsurances: sortedAuthorizedInsurances,
            displayOnNetworkAccordion: displayOnNetworkAccordion,
            excludeDeactivatedFacilityImpact,
            excludeDeactivatedFacilityNetwork,
            excludeDeactivatedProviderImpact,
            excludeDeactivatedProviderNetwork,
            excludeInvalidNppesNpiFacilityImpact,
            excludeInvalidNppesNpiFacilityNetwork,
            excludeInvalidNppesNpiProviderImpact,
            excludeInvalidNppesNpiProviderNetwork,
            excludeMedicareOptOutImpact,
            excludeMedicareOptOutNetwork,
            excludeMissingNpiFacilityImpact,
            excludeMissingNpiFacilityNetwork,
            excludeMissingNpiProviderImpact,
            excludeMissingNpiProviderNetwork,
            excludeOIGExclusionImpact,
            excludeOIGExclusionNetwork,
            excludePrecludedImpact,
            excludePrecludedNetwork,
            hideCMSApplicationCreditOption: !hasCmsApplicationCredits,
            hideCareJourneyScores: !showCareJourneyScores,
            hideCreateStandards: hideStandardSettingCreation,
            hideEnterpriseComplianceDashboard: !showEnterpriseComplianceDashboard,
            hideMotiveAppropriatenessScore: !showMotiveAppropriatenessScore,
            hideStrictTimeDistance: hideStrictTimeDistance,
            hideUseInMarketAnalysis: !showUseInMarketAnalysis,
            isNetworkTaxonomyValid: initialTaxonomyIsValid,
            isZipProject: isZipTemplate,
            marketProviderSets,
            project,
            projectAccessCodeMapSets: templateInfo ? templateInfo.projectAccessCodeMapSets : null,
            projectMemberPopulations: templateInfo ? templateInfo.projectMemberPopulations : null,
            providerSets,
            resourceTags,
            selectedInsurances,
            showAffiliationsInsights: showAffiliationsInsights,
            showNetworkSpecialtyCodeMap: showNetworkSpecialtyCodeMap,
            sortedSpecialtyGroups,
            specialtyCodeMapSets,
            specialtyGroups,
            specialtyToQdpCodeMapSets,
            templateAccessGranularity: templateInfo ? templateInfo.templateAccessGranularity : null,
            templateCollections,
            timeDistanceCalculatorTypes: templateInfo ? templateInfo.timeDistanceCalculatorTypes : [TimeDistanceCalculatorType.Quest],
          });
        } else {
          //Shared users won't have access to all the data to populate the dropdowns.
          //They will just see disabled textboxes with the string values of those project options.

          let allMemberPopulations: MemberSetModel[] = [];
          let isZipTemplate = false;
          let specialtyGroups;
          let templateMetadata: TemplateMetadata = null;
          const accuracySpecialtyDefinitions = await this.accuracyTemplateService.getSpecialtyDefinitions();
          const specialtyToQdpCodeMapSets = await this.codeMapSetService.get(CodeMapType.SpecialtyToQDPCodeMap);

          if (project.adequacyTemplateCollectionId && project.adequacyTemplateCorrelationId) {
            haveAdequacy = true;
            isZipTemplate = this.IsProjectUsingZipTemplate(templateCollections, project);
            allMemberPopulations = await this.memberSetService.getActive(true, false)
          }

          if (project.templateId) {
            specialtyGroups =
              await this.templateService.getTemplateSpecialtyGroups(project.adequacyTemplateCollectionId, project.templateId);
            templateMetadata = await this.templateService.getTemplateById(project.adequacyTemplateCollectionId, project.templateId, true);
          } else {
            //Get the latest template if templateId prop is null.
            //For shared users this would only occur if it's a preview user with an unrun project.
            if (project.accuracySpecialtyDefinitionId) {
              specialtyGroups = await this.getAccuracySpecialtyGroups(project.accuracySpecialtyDefinitionId);
            }
            if (project.adequacyTemplateCollectionId) {
              specialtyGroups =
                await this.templateService.getLatestTemplateSpecialtyGroupsByCorrelationId(project.adequacyTemplateCollectionId,
                  project.adequacyTemplateCorrelationId);
              templateMetadata =
                await this.templateService.getLatestTemplateByCorrelationId(project.adequacyTemplateCollectionId, project.adequacyTemplateCorrelationId, true);
            }
          }

          this.currentProjectName = project.resource?.name;

          if (isZipTemplate)
            project.excludeInVolumeActions = true;

          project.editProjectOverrideMemberSets = haveAdequacy ? this.getActiveMembershipOverrides(project) : this.getFreshMembershipOverrides(project);

          this.initialProjectState = {
            displayOnNetworkAccordion,
            excludeDeactivatedFacilityImpact,
            excludeDeactivatedFacilityNetwork,
            excludeDeactivatedProviderImpact,
            excludeDeactivatedProviderNetwork,
            excludeInvalidNppesNpiFacilityImpact,
            excludeInvalidNppesNpiFacilityNetwork,
            excludeInvalidNppesNpiProviderImpact,
            excludeInvalidNppesNpiProviderNetwork,
            excludeMedicareOptOutImpact,
            excludeMedicareOptOutNetwork,
            excludeMissingNpiFacilityImpact,
            excludeMissingNpiFacilityNetwork,
            excludeMissingNpiProviderImpact,
            excludeMissingNpiProviderNetwork,
            excludeOIGExclusionImpact,
            excludeOIGExclusionNetwork,
            excludePrecludedImpact,
            excludePrecludedNetwork,
            project,
            showAffiliationsInsights,
          };

          this.setState({
            accuracySpecialtyDefinitions,
            allMemberPopulations,
            displayOnNetworkAccordion: displayOnNetworkAccordion,
            excludeDeactivatedFacilityImpact,
            excludeDeactivatedFacilityNetwork,
            excludeDeactivatedProviderImpact,
            excludeDeactivatedProviderNetwork,
            excludeInvalidNppesNpiFacilityImpact,
            excludeInvalidNppesNpiFacilityNetwork,
            excludeInvalidNppesNpiProviderImpact,
            excludeInvalidNppesNpiProviderNetwork,
            excludeMedicareOptOutImpact,
            excludeMedicareOptOutNetwork,
            excludeMissingNpiFacilityImpact,
            excludeMissingNpiFacilityNetwork,
            excludeMissingNpiProviderImpact,
            excludeMissingNpiProviderNetwork,
            excludeOIGExclusionImpact,
            excludeOIGExclusionNetwork,
            excludePrecludedImpact,
            excludePrecludedNetwork,
            isZipProject: isZipTemplate,
            project,
            showAffiliationsInsights: showAffiliationsInsights,
            sortedSpecialtyGroups: specialtyGroups && sortBy(specialtyGroups, this.sortOrder),
            specialtyGroups,
            specialtyToQdpCodeMapSets,
            templateAccessGranularity: templateMetadata ? templateMetadata.accessCriteriaSpecificationGranularity : null,
            useAccuracy: !!(project.accuracyTemplateCollectionId && project.accuracyTemplateCorrelationId),
            useAdequacy: haveAdequacy,
          });
        }

        this.projectNames = await this.resourceService.getProjectNames();
      }
    } catch (error) {
      this.setState({
        error: "Failed to retrieve project details. Please try again. If this project's Template Collection has been archived, it must be unarchived to be edited.",
        submitClicked: false,
      });
    }

    this.generateAltMemberships();
    this.generateSpecialtyOverrides(this.state.specialtyGroups);
    this.toggleMemberBinding();
    this.toggleShowClosestProviderReportOption();
  }

  public async componentDidUpdate(prevProps: ProjectOptionsProps) {
    this.props.onChange(this.isDirty());

    if (prevProps.enabledFeatureFlags !== this.props.enabledFeatureFlags) {
      const showCareJourneyScores = this.props.enabledFeatureFlags.has(Features.ENABLE_CARE_JOURNEY_SCORES);
      const showUseInMarketAnalysis = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
      const showEnterpriseComplianceDashboard = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
      const showNetworkSpecialtyCodeMapSet = this.props.enabledFeatureFlags.has(Features.NETWORK_BENCHMARKING) && this.props.user.roles.includes(Roles.COMPETITIVE_INSIGHTS_USER);
      const hideStrictTimeDistance = this.props.enabledFeatureFlags.has(Features.HIDE_STRICT_TIME_DISTANCE);
      const hideStandardSettingCreation = !(this.props.enabledFeatureFlags.has(Features.STANDARD_SETTING_CREATION) && this.props.user.roles.includes(Roles.STANDARDS_CREATOR_USER));

      let showMotiveAppropriatenessScore = false;

      if (this.state.project?.adequacyTemplateCollectionId && this.state.project?.adequacyTemplateCorrelationId) {
        const templateInfo = await this.templateService.getTemplateById(this.state.project?.adequacyTemplateCollectionId, this.state.project?.adequacyTemplateCorrelationId, null);
        showMotiveAppropriatenessScore = templateInfo.motiveCrosswalkCodeMapSetId !== undefined && templateInfo.motiveCrosswalkCodeMapSetId !== null;
      }

      this.setState({
        hideCareJourneyScores: !showCareJourneyScores,
        hideCreateStandards: hideStandardSettingCreation,
        hideEnterpriseComplianceDashboard: !showEnterpriseComplianceDashboard,
        hideMotiveAppropriatenessScore: !showMotiveAppropriatenessScore,
        hideStrictTimeDistance: hideStrictTimeDistance,
        hideUseInMarketAnalysis: !showUseInMarketAnalysis,
        showNetworkSpecialtyCodeMap: showNetworkSpecialtyCodeMapSet,
      })
    }
  }

  public render() {
    return this.state.project ? this.renderForm() : <LoadingSpinner />;
  }

  private renderForm() {
    const projectNameOk = (this.state.project?.resource?.name !== null && this.state.project?.resource?.name !== "" && (!this.projectNames.includes(this.state.project?.resource?.name) || this.state.project?.resource?.name === this.currentProjectName));
    const projectDescriptionOk = (this.state.project?.resource?.description !== null && this.state.project?.resource?.description !== "");

    return (this.state.project || null) && (
      <>
        <div className="lead">
          {(!this.isDirty() || null) &&
            <>
              <div className="pull-right fs15" >
                {(!this.state.project?.resource?.isShared || null) && this.state.project?.adequacyTemplateCollectionId && this.state.project?.templateId &&
                  <Link className={`qes-button qes-button-secondary`} type="button" to={{ pathname: `/templates/adequacy/${this.state.project?.adequacyTemplateCollectionId}/${this.state.project?.templateId}`, state: { telehealthSpecialtyGroupCodes: this.state.project?.telehealthSpecialtyGroupCodes } }}><i className="fa fa-umbrella" /> View Adequacy Template</Link>
                }
                {(!this.state.project?.resource?.isShared || null) && this.state.project?.accuracyTemplateCollectionId && this.state.project?.accuracyTemplateId &&
                  <Link className={`qes-button qes-button-secondary`} type="button" to={`/templates/accuracy/${this.state.project?.accuracyTemplateCollectionId}/${this.state.project?.accuracyTemplateId}`}><i className="fa fa-crosshairs" /> View Accuracy Template</Link>
                }
                {(!(this.state.project?.resource?.isShared || this.state.project?.isRunning || this.state.project?.isReadOnly) || null) && (
                  <>
                    <Link className={`qes-button qes-button-secondary`} type="button" to={`/projects/${this.state.project?.projectId}/edit/service-area`}><i className="fa fa-globe" /> Edit Service Area</Link>
                    <a className="qes-button qes-button-secondary" type="button" href="#" onClick={this.handleRunProject}><i className="fa fa-rocket" /> Run Project</a>
                  </>
                )}
              </div>
              <div className="clearfix" />
            </>
          }
        </div>
        {this.state.error && <QESAlert type="danger" persist={true}>{this.state.error}</QESAlert>}

        {!!this.state.warningForDisableOption &&
          <Modal
            isVisible={!!this.state.warningForDisableOption}
            content={{
              body: this.state.warningForDisableDescription,
              cancelButtonText: "Cancel",
              confirmButtonAction: () => this.toggleCheckBox(this.state.warningForDisableOption),
              confirmButtonText: "Disable",
              confirmButtonType: "primary",
              title: "Warning",
            }}
            closeModal={() => {
              this.props.setSuspendEscape(false);
              this.setState({ warningForDisableDescription: "", warningForDisableOption: null });
            }}
            hideBodyScroll={false}
            triggerElement={this.ecdCheckboxRef}
          />
        }

        <>
          <div>
            <p>Please edit your project details...</p>
          </div>
          <div className="form-horizontal">
            <div className="row">
              <div className="col-12">
                <div className="tabs-style-2">
                  <ul className="nav nav-tabs">

                    <li className="nav-item"><a className={this.state.activeEditModule === "Details" ? "nav-link active" : "nav-link"} href="#" role="tab" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, this.handleDetailsPaneClick)}>Details</a></li>

                    <li className="nav-item"><a className={this.state.activeEditModule === "Template" ? "nav-link active" : "nav-link"} href="#" role="tab" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, this.handleTemplatePaneClick)}>Template</a></li>

                    <li className="nav-item"><a className={this.state.activeEditModule === "Members" ? "nav-link active" : "nav-link"} href="#" role="tab" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, this.handleMembersPaneClick)}>Members</a></li>

                    <li className="nav-item"><a className={this.state.activeEditModule === "Providers" ? "nav-link active" : "nav-link"} href="#" role="tab" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, this.handleProvidersPaneClick)}>Providers</a></li>

                    <li className="nav-item"><a className={this.state.activeEditModule === "Network Taxonomy" ? "nav-link active" : "nav-link"} href="#" role="tab" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, this.handleNetworkTaxonomyPaneClick)}>Network Taxonomy</a></li>

                    {!this.state.project?.resource?.isShared ?
                      <li className="nav-item"><a className={this.state.activeEditModule === "Options" ? "nav-link active" : "nav-link"} href="#" role="tab" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, this.handleOptionsPaneClick)}>Options</a></li> :
                      <div />
                    }
                  </ul>
                </div>

              </div>
              {this.state.activeEditModule === "Details" &&
                <Details
                  disabled={this.props.disabled || this.state.project?.isRunning || this.state.project?.isReadOnly}
                  onDescChange={this.handleDescChange}
                  onIsPrivateChange={this.handleIsPrivateChange}
                  onNameChange={this.handleNameChange}
                  onTaggingChange={this.handleTaggingChange}
                  project={this.state.project}
                  projectDescriptionOk={projectDescriptionOk}
                  projectNameOk={projectNameOk}
                  projectNameValue={this.state.project?.resource?.name}
                  resourceTags={this.state.resourceTags}
                />
              }
              {this.state.activeEditModule === "Template" &&
                <UserContext.Consumer>
                  {(user) => (
                    <TemplateOptions
                      accuracyInsurancesIsLoading={this.state.accuracyInsurancesIsLoading}
                      accuracyTemplateCollections={this.state.accuracyTemplateCollections}
                      accuracySpecialtyDefinitions={this.state.accuracySpecialtyDefinitions}
                      authorizedInsurances={this.state.authorizedInsurances}
                      checkboxClass={this.state.project?.isRunning || this.state.project?.isReadOnly ? "checkbox disabled" : "checkbox"}
                      disabled={this.props.disabled || this.state.project?.isRunning || this.state.project?.isReadOnly}
                      specialtyCodeMapSets={this.state.specialtyCodeMapSets}
                      handleAddInspectedInsurance={this.handleAddInspectedInsurance}
                      handleRemoveInspectedInsurance={this.handleRemoveInspectedInsurance}
                      handleUseAccuracyChange={this.handleUseAccuracyChange}
                      handleUseAdequacyChange={this.handleUseAdequacyChange}
                      handleAuthorizedInsurancesChange={this.handleAuthorizedInsurancesChange}
                      isZipProject={this.state.isZipProject}
                      onAccessCodeMapSetChange={this.handleAccessCodeMapSetChange}
                      onAccuracySpecialtyDefinitionIdChange={this.handleAccuracySpecialtyDefinitionIdChange}
                      onAccuracySpecialtyToQdpCodeMapChange={this.handleAccuracySpecialtyToQdpCodeMapChange}
                      onAccuracyTemplateCollectionChange={this.handleAccuracyTemplateCollectionChange}
                      onAccuracyTemplateCorrelationIdChange={this.handleAccuracyTemplateCorrelationIdChange}
                      onCheckBoxChange={this.handleCheckBoxChange}
                      onMarketChanged={this.handleMarketChanged}
                      onMarketSpecialtyCodeMapChange={this.handleMarketSpecialtyCodeMapChange}
                      onNetworkSpecialtyCodeMapChange={this.handleNetworkSpecialtyCodeMapChange}
                      onSpecialtyGroupsChange={this.handleSpecialtyGroupsChange}
                      onTelehealthSpecialtyGroupsChange={this.handleTelehealthSpecialtyGroupChange}
                      onTemplateCollectionChange={this.handleTemplateCollectionChange}
                      onTemplateCorrelationIdChange={this.handleTemplateCorrelationIdChange}
                      onTimeDistanceCalculatorTypeChange={this.handleTimeDistanceCalculatorTypeChange}
                      perspective={EditPerspective.Edit}
                      project={this.state.project}
                      projectAccessCodeMapSets={this.state.projectAccessCodeMapSets}
                      selectedInsuranceUid={this.state.selectedInsuranceUid}
                      selectedInsurances={this.state.selectedInsurances}
                      shared={this.state.project?.resource?.isShared}
                      showNetworkSpecialtyCodeMap={this.state.showNetworkSpecialtyCodeMap}
                      sortedSpecialtyGroups={this.state.sortedSpecialtyGroups}
                      specialtyToQdpCodeMapSets={this.state.specialtyToQdpCodeMapSets}
                      templateCollections={this.state.templateCollections}
                      templateAccessGranularity={this.state.templateAccessGranularity}
                      timeDistanceCalculatorTypes={this.state.timeDistanceCalculatorTypes}
                      useAccuracy={this.state.useAccuracy && !this.state.isZipProject}
                      useAdequacy={this.state.useAdequacy}
                      user={user}
                      adequacyTemplateCollectionIdRef={undefined}
                    />
                  )}
                </UserContext.Consumer>
              }
              {this.state.activeEditModule === "Members" &&
                <MemberOptions
                  {...this.props}
                  checkboxClass={this.state.project?.isRunning || this.state.project?.isReadOnly ? "checkbox disabled" : "checkbox"}
                  disabled={this.props.disabled || this.state.project?.isRunning || this.state.project?.isReadOnly}
                  generateAlternateMemberships={this.generateAltMemberships}
                  handleMemberPopAliasChange={this.handleMemberPopAliasChange}
                  handleMemberPopAliasChange2={this.handleMemberPopAliasChange2}
                  handleMemberPopChange={this.handleMemberPopChange}
                  handleMemberPopChange2={this.handleMemberPopChange2}
                  handleRiskPopChange={this.handleRiskPopChanged}
                  onAddAlternateRow={this.handleNewAlternativeMemberPop}
                  onAlternateMemberPopAliasChange={this.handleAlternateMemberPopAliasChange}
                  onAlternateMemberPopChange={this.handleAlternateMemberPopChange}
                  onCheckBoxChange={this.handleCheckBoxChange}
                  onDeleteAlternateRow={this.handleDeleteAlternateRow}
                  onSpecialtyGroupsChange={this.handleSpecialtyGroupsChange}
                  project={this.state.project}
                  projectMemberPopulations={this.state.projectMemberPopulations}
                  shared={this.state.project?.resource?.isShared}
                  sortedSpecialtyGroups={this.state.sortedSpecialtyGroups}
                  allMemberPopulations={this.state.allMemberPopulations}
                  alternateMemberships={this.state.alternateMemberships}
                  disableMemberBindingTable={this.state.disableMemberBindingTable}
                  handlePrimaryMembershipBindingChange={this.handlePrimaryMembershipBindingChange}
                  handleSecondaryMembershipBindingChange={this.handleSecondaryMembershipBindingChange}
                  setPrimarySpecialtyOverride={this.setPrimarySpecialtyOverride}
                  setSecondarySpecialtyOverride={this.setSecondarySpecialtyOverride}
                  specialtyGroups={this.state.specialtyGroups}
                  specialtyOverrides={this.state.specialtyOverrides}
                  isZipProject={this.state.isZipProject}
                />
              }
              {this.state.activeEditModule === "Providers" &&
                <ProviderOptions
                  enabledFeatureFlags={this.props.enabledFeatureFlags}
                  checkboxClass={this.state.project?.isRunning || this.state.project?.isReadOnly ? "checkbox disabled" : "checkbox"}
                  disabled={this.props.disabled || this.state.project?.isRunning || this.state.project?.isReadOnly}
                  marketProviderSets={this.state.marketProviderSets}
                  onCheckBoxChange={this.handleCheckBoxChange}
                  onMarketChanged={this.handleMarketChanged}
                  onProspectsChanged={this.handleProspectsChanged}
                  onProvidersChanged={this.handleProvidersChanged}
                  providerSets={this.state.providerSets}
                  project={this.state.project}
                  shared={this.state.project?.resource?.isShared}
                  networkDisabled={false}
                  hideUseInMarketAnalysis={this.state.hideUseInMarketAnalysis}
                  hideUseMarketProvidersForImpact={false}
                  onChangeMarketAlias={this.handleChangeMarketProvidersAlias}
                  onChangeNetworkAlias={this.handleChangeNetworkProvidersAlias}
                  onChangeProspectAlias={this.handleChangeProspectProvidersAlias}
                  isZipProject={this.state.isZipProject}
                />
              }
              {this.state.activeEditModule === "Network Taxonomy" &&
                <div className="tab-contents col-12">
                  <div className="col-10 offset-2">
                    <NetworkTaxonomyContentPanel
                      networkTaxonomyData={this.state.project?.networkTaxonomy}
                      onNetworkTaxonomyChange={this.handleNetworkTaxonomyChange}
                      disabled={this.props.disabled || this.state.project?.isRunning || this.state.project?.isReadOnly}
                    />
                  </div>
                </div>
              }
              {this.state.activeEditModule === "Options" &&
                <EditDisplayOptions
                  sidePanelId={this.props.sidePanelId}
                  accuracyAndAdequacy={this.state.useAdequacy && this.state.useAccuracy}
                  checkboxClass={this.state.project?.isRunning || this.state.project?.isReadOnly ? "checkbox disabled" : "checkbox"}
                  disabled={this.props.disabled || this.state.project?.isRunning || this.state.project?.isReadOnly}
                  disableUnsupportedOptions={this.state.disableUnsupportedOptions}
                  excludeDeactivatedFacilitiesImpact={this.state.excludeDeactivatedFacilityImpact}
                  excludeDeactivatedFacilitiesNetwork={this.state.excludeDeactivatedFacilityNetwork}
                  excludeDeactivatedProvidersImpact={this.state.excludeDeactivatedProviderImpact}
                  excludeDeactivatedProvidersNetwork={this.state.excludeDeactivatedProviderNetwork}
                  excludeInvalidNppesNpiFacilitiesImpact={this.state.excludeInvalidNppesNpiFacilityImpact}
                  excludeInvalidNppesNpiFacilitiesNetwork={this.state.excludeInvalidNppesNpiFacilityNetwork}
                  excludeInvalidNppesNpiProvidersImpact={this.state.excludeInvalidNppesNpiProviderImpact}
                  excludeInvalidNppesNpiProvidersNetwork={this.state.excludeInvalidNppesNpiProviderNetwork}
                  excludeMissingNpiFacilitiesImpact={this.state.excludeMissingNpiFacilityImpact}
                  excludeMissingNpiFacilitiesNetwork={this.state.excludeMissingNpiFacilityNetwork}
                  excludeMissingNpiProvidersImpact={this.state.excludeMissingNpiProviderImpact}
                  excludeMissingNpiProvidersNetwork={this.state.excludeMissingNpiProviderNetwork}
                  excludeMedicareOptOutProvidersImpact={this.state.excludeMedicareOptOutImpact}
                  excludeMedicareOptOutProvidersNetwork={this.state.excludeMedicareOptOutNetwork}
                  excludeOIGExcludedProvidersImpact={this.state.excludeOIGExclusionImpact}
                  excludeOIGExcludedProvidersNetwork={this.state.excludeOIGExclusionNetwork}
                  excludePrecludedProvidersImpact={this.state.excludePrecludedImpact}
                  excludePrecludedProvidersNetwork={this.state.excludePrecludedNetwork}
                  onCheckBoxChange={this.handleCheckBoxChange}
                  onImpactExclusionsChange={this.handleImpactExclusionsChange}
                  onNetworkExclusionsChange={this.handleNetworkExclusionsChange}
                  project={this.state.project}
                  shared={this.state.project?.resource?.isShared}
                  hideClosestProviderReportOption={this.state.hideClosestProviderReportOption}
                  hideCMSApplicationCredit={this.state.hideCMSApplicationCreditOption}
                  hideCareJourneyScores={this.state.hideCareJourneyScores}
                  hideCreateStandards={this.state.hideCreateStandards}
                  hideMotiveAppropriatenessScore={this.state.hideMotiveAppropriatenessScore}
                  hideEnterpriseComplianceDashboard={this.state.hideEnterpriseComplianceDashboard}
                  hidePracAtLocCondifence={!this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PRAC_AT_LOC_CONFIDENCE)}
                  hideProviderClaimsActivity={!this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PROVIDER_CLAIMS_ACTIVITY)}
                  hideStrictTimeDistance={this.state.hideStrictTimeDistance}
                  useAccuracy={this.state.useAccuracy}
                  useAdequacy={this.state.useAdequacy}
                  ecdCheckboxRef={this.ecdCheckboxRef}
                  isZipProject={this.state.isZipProject}
                  showNetworkIntegrityDashboard={this.props.enabledFeatureFlags.has(Features.ENABLE_NETWORK_INTEGRITY_DASHBOARD)}
                  showAffiliationsInsights={this.state.showAffiliationsInsights}
                  displayOnNetworkAccordion={this.state.displayOnNetworkAccordion}
                />
              }
            </div>
          </div>

          <FlyoutFooter
            submitButton={
              <QESButton
                qesTestId="edit-project_save-changes"
                qesType="primary"
                className="mt-1 mr5"
                disabled={this.props.disabled || this.state.submitClicked || this.isSubmitDisabled() || !projectNameOk}
                onClick={(e) => this.handleFormSubmit(e).then(() => this.props.setIsFlyoutVisible(false))}
              >
                {(this.state.submitClicked || null) && <><i className="fa fa-spinner fa-spin" />&nbsp;</>}
                Save Changes
              </QESButton>
            }
          />
        </>
      </>
    );
  }

  private handleRunProject = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!this.state.project?.isRunning) {
      this.setState((prevState) => ({ ...prevState, userCanEdit: false }), async () => {
        if (this.props.onRunProject) {
          this.props.onRunProject().then(() => this.props.setIsFlyoutVisible(false));
        } else {
          if (this.state.project?.resource?.resourceType === ResourceType.ZipAnalysisProject) {
            await this.zipProjectService.runZipProject(this.props.projectId);
          } else {
            await this.projectService.execute(this.props.projectId);
          }

          if (this.props.onRunProjectSuccess) {
            this.props.onRunProjectSuccess().then(() => this.props.setIsFlyoutVisible(false));
          }
        }
      });
    }
  }

  private handleNameChange = (projectName: string) => {
    const project: Project = { ...this.state.project };

    project.resource = {
      ...this.state.project?.resource,
      name: projectName,
    };

    this.setState({ project });
  }

  private handleDescChange = (desc: string) => {
    const project: Project = { ...this.state.project };

    project.resource = {
      ...this.state.project?.resource,
      description: desc,
    };

    this.setState({ project });
  }

  private handleTaggingChange = (tags: ResourceTagModel[]) => {
    const project: Project = { ...this.state.project, tags };
    this.setState({ project });
  }

  private handleIsPrivateChange = (isPrivate: boolean) => {
    const project: Project = { ...this.state.project };

    project.resource = {
      ...this.state.project?.resource,
      isPrivate,
    };

    this.setState({ project });
  }

  private handleCheckBoxChange = (property: UpdateResourceKeys) => {
    const latestPublishedIndex = this.state.project?.projectRunConfigurations?.findIndex((pc) => pc.isLatestPublished);
    // run configs are delivered in descending order of run configuration id, so the previous will be the one after the latest
    const previousConfig = !!latestPublishedIndex ? this.state.project?.projectRunConfigurations[latestPublishedIndex + 1] : undefined;

    if (property === "includeInEnterpriseComplianceDashboard" &&
      this.state.project[property] &&
      this.initialProjectState?.project[property] &&
      (!previousConfig || previousConfig?.includeInEnterpriseComplianceDashboard)
    ) {
      this.props.setSuspendEscape(true);
      this.setState({ warningForDisableDescription: "Disabling this option will cause this project to be immediately removed from the Enterprise Compliance Dashboard when the changes are saved.", warningForDisableOption: property });
    } else {
      this.toggleCheckBox(property);
    }
  }

  private toggleCheckBox = (property: UpdateResourceKeys) => {
    const project: Project = {
      ...this.state.project,
      [property]: !this.state.project[property],
    };

    // if showPciData is being changed, turn off showPciDashboard, showLocationConfidenceScores, and showMotiveAppropriatenessScore, else keep current state
    if (property === "showPciData") {
      project.showCareJourneyScores = false;
      project.showLocationConfidenceScores = false;
      project.showMotiveAppropriatenessScore = false;
      project.showPciDashboard = false;
      project.showTricareClaimsVolumeColumn = false;
      project.showVaClaimsVolumeColumn = false;
    }

    // if showNextClosestNetworkProviders or hasZipSummaries is being changed, turn enableRegulatoryExceptionReports or showPatternOfCareProviders off if it was true
    if ((property === "showNextClosestNetworkProviders" || property === "hasZipSummaries") && (!!project.enableRegulatoryExceptionReports || !!project.showPatternOfCareProviders)) {
      project.enableRegulatoryExceptionReports = false;
      project.showPatternOfCareProviders = false;
    }

    this.props.setSuspendEscape(false);
    this.setState({ project, warningForDisableDescription: "", warningForDisableOption: null });
  }

  private handleRiskPopChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const riskPop = +(e.target.value);
    const riskDescription = (e.target.options[e.target.selectedIndex].innerText);

    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = project?.editProjectOverrideMemberSets ?? [];
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
        x.memberSetId = riskPop;
      }
    });

    if (riskPop) {
      this.setState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          editProjectOverrideMemberSets: updates,
          membershipRiskMemberSetAlias: riskDescription,
          membershipRiskMemberSetId: riskPop,
        },
      }));
    } else {
      this.setState((prevState): ProjectOptionsState => ({
        ...prevState,
        project: {
          ...prevState.project,
          membershipRiskMemberSetAlias: "",
          membershipRiskMemberSetId: null,
        },
      }));
    }
  }

  private handleSpecialtyGroupsChange = (selectedRecords: TemplateSpecialtyGroup[]) => {
    const specialtyGroupCodes = this.state.sortedSpecialtyGroups.map((sg) => sg.code);
    const selectedSpecialtyGroupCodes = selectedRecords.map((sg) => sg.code);
    const excludedSpecialtyGroupCodes = difference(specialtyGroupCodes, selectedSpecialtyGroupCodes);

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        excludedSpecialtyGroupCodes,
      },
    }));
  }

  private handleNetworkExclusionsChange = (exclusion: ProjectExclusionType) => {
    switch (exclusion) {
      case ProjectExclusionType.DeactivatedFacility:
        this.setState({ excludeDeactivatedFacilityNetwork: !this.state.excludeDeactivatedFacilityNetwork });
        break;
      case ProjectExclusionType.DeactivatedProvider:
        this.setState({ excludeDeactivatedProviderNetwork: !this.state.excludeDeactivatedProviderNetwork });
        break;
      case ProjectExclusionType.InvalidNppesNpiFacility:
        this.setState({ excludeInvalidNppesNpiFacilityNetwork: !this.state.excludeInvalidNppesNpiFacilityNetwork });
        break;
      case ProjectExclusionType.InvalidNppesNpiProvider:
        this.setState({ excludeInvalidNppesNpiProviderNetwork: !this.state.excludeInvalidNppesNpiProviderNetwork });
        break;
      case ProjectExclusionType.MissingNpiFacility:
        this.setState({ excludeMissingNpiFacilityNetwork: !this.state.excludeMissingNpiFacilityNetwork });
        break;
      case ProjectExclusionType.MissingNpiProvider:
        this.setState({ excludeMissingNpiProviderNetwork: !this.state.excludeMissingNpiProviderNetwork });
        break;
      case ProjectExclusionType.MedicareOptOut:
        this.setState({ excludeMedicareOptOutNetwork: !this.state.excludeMedicareOptOutNetwork });
        break;
      case ProjectExclusionType.OIGExclusion:
        this.setState({ excludeOIGExclusionNetwork: !this.state.excludeOIGExclusionNetwork });
        break;
      case ProjectExclusionType.Precluded:
        this.setState({ excludePrecludedNetwork: !this.state.excludePrecludedNetwork });
        break;
    }
  }

  private handleImpactExclusionsChange = (exclusion: ProjectExclusionType) => {
    switch (exclusion) {
      case ProjectExclusionType.DeactivatedFacility:
        this.setState({ excludeDeactivatedFacilityImpact: !this.state.excludeDeactivatedFacilityImpact });
        break;
      case ProjectExclusionType.DeactivatedProvider:
        this.setState({ excludeDeactivatedProviderImpact: !this.state.excludeDeactivatedProviderImpact });
        break;
      case ProjectExclusionType.InvalidNppesNpiFacility:
        this.setState({ excludeInvalidNppesNpiFacilityImpact: !this.state.excludeInvalidNppesNpiFacilityImpact });
        break;
      case ProjectExclusionType.InvalidNppesNpiProvider:
        this.setState({ excludeInvalidNppesNpiProviderImpact: !this.state.excludeInvalidNppesNpiProviderImpact });
        break;
      case ProjectExclusionType.MissingNpiFacility:
        this.setState({ excludeMissingNpiFacilityImpact: !this.state.excludeMissingNpiFacilityImpact });
        break;
      case ProjectExclusionType.MissingNpiProvider:
        this.setState({ excludeMissingNpiProviderImpact: !this.state.excludeMissingNpiProviderImpact });
        break;
      case ProjectExclusionType.MedicareOptOut:
        this.setState({ excludeMedicareOptOutImpact: !this.state.excludeMedicareOptOutImpact });
        break;
      case ProjectExclusionType.OIGExclusion:
        this.setState({ excludeOIGExclusionImpact: !this.state.excludeOIGExclusionImpact });
        break;
      case ProjectExclusionType.Precluded:
        this.setState({ excludePrecludedImpact: !this.state.excludePrecludedImpact });
        break;
    }
  }

  private handleProvidersChanged = (value: string) => {
    if (value) {
      const networkProviderSetId = Number(value);

      this.setState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          networkProviderSetId,
        },
      }));
    } else {
      this.setState((prevState): ProjectOptionsState => ({
        ...prevState,
        project: {
          ...prevState.project,
          networkProviderSetId: null,
        },
      }));
    }
  }

  private handleChangeNetworkProvidersAlias = (alias: string) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        networkProviderSetAlias: alias,
      }
    }));
  }

  private handleProspectsChanged = (value: string) => {
    if (value) {
      const prospectProviderSetId = Number(value);

      this.setState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          prospectProviderSetId,
        },
      }));
    } else {
      this.setState((prevState): ProjectOptionsState => ({
        ...prevState,
        project: {
          ...prevState.project,
          prospectProviderSetId: null,
          useProspectProvidersForMarketAnalysis: false,
        },
      }));
    }
  }

  private handleChangeProspectProvidersAlias = (alias: string) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        prospectProviderSetAlias: alias,
      }
    }));
  }

  private handleMarketChanged = (value: string) => {
    if (value) {
      const marketProviderSetId = Number(value);

      this.setState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          marketProviderSetId,
          useMarketProvidersForImpact: true,
          useMarketProvidersForMarketAnalysis: true,
        },
      }));
    } else {
      this.setState((prevState): ProjectOptionsState => ({
        ...prevState,
        project: {
          ...prevState.project,
          marketProviderSetId: null,
          useMarketProvidersForImpact: false,
          useMarketProvidersForMarketAnalysis: false
        },
      }));
    }
  }

  private handleChangeMarketProvidersAlias = (alias: string) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        marketProviderSetAlias: alias,
      }
    }));
  }

  private handleNetworkSpecialtyCodeMapChange = (value: number) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        networkSpecialtyCodeMapSetId: value || null,
      },
    }));
  }

  private handleMarketSpecialtyCodeMapChange = (value: number) => {
    const prevState = this.state;

    this.setState({
      project: {
        ...prevState.project,
        marketSpecialtyCodeMapSetId: value || null,
      },
    });
  }

  private handleAccuracyTemplateCollectionChange = (templateCollectionId: string) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        accuracyTemplateCollectionId: templateCollectionId,
        accuracyTemplateCorrelationId: "",
      },
    }));
  }

  private handleAccuracyTemplateCorrelationIdChange = async (templateCorrelationId: string) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        accuracyTemplateCorrelationId: templateCorrelationId,
      },
    }));
  }

  private handleAccuracySpecialtyToQdpCodeMapChange = (value: number) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        accuracySpecialtyToQdpCodeMapSetId: value || null,
      },
    }));
  }
  private handleAccuracySpecialtyDefinitionIdChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accuracySpecialtyDefinitionId = e.target.value;
    let sortedSpecialtyGroups: TemplateSpecialtyGroup[] = [];

    this.setState((prevState): ProjectOptionsState => ({
      ...prevState,
      project: {
        ...prevState.project,
        accuracySpecialtyDefinitionId,
        //if accuracy specialty is set, reset adequacy
        adequacyTemplateCollectionId: null,
        adequacyTemplateCorrelationId: "0",
        excludedSpecialtyGroupCodes: [],
      },
      projectAccessCodeMapSets: [],
      projectMemberPopulations: [],
      sortedSpecialtyGroups: [],
    }));

    sortedSpecialtyGroups = await this.getAccuracySpecialtyGroups(accuracySpecialtyDefinitionId);

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
      },
      sortedSpecialtyGroups,
    }));
  }

  private handleTemplateCollectionChange = (templateCollectionId: number) => {
    this.setState((prevState): ProjectOptionsState => ({
      ...prevState,
      project: {
        ...prevState.project,
        accessCodeMapSetId: null,
        accuracySpecialtyDefinitionId: null,
        adequacyTemplateCollectionId: templateCollectionId,
        adequacyTemplateCorrelationId: "0",
        excludedSpecialtyGroupCodes: [],
        memberSetId: null,
        timeDistanceCalculatorType: TimeDistanceCalculatorType.Unknown,
      },
      projectAccessCodeMapSets: [],
      projectMemberPopulations: [],
      sortedSpecialtyGroups: [],
      timeDistanceCalculatorTypes: [],
    }));
  }

  private handleTelehealthSpecialtyGroupChange = (selectedSpecialtyGroupCodes: string[]) => {
    this.setState((prevState) => {
      return (
        {
          ...prevState,
          project: {
            ...prevState.project,
            telehealthSpecialtyGroupCodes: selectedSpecialtyGroupCodes,
          },
        });
    },
    );
  }

  private handleUseAccuracyChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = !!e.target.checked;
    let sortedAuthorizedInsurances: ClientInsuranceModel[] = [...this.state.authorizedInsurances];
    let error = this.state.error;

    if (checked && sortedAuthorizedInsurances.length === 0) {
      try {
        this.setState((prevState) => ({ ...prevState, accuracyInsurancesIsLoading: true, useAccuracy: checked }));
        const authorizedInsurances = await this.insuranceService.getAuthorizedInsurancesByClientId();
        sortedAuthorizedInsurances = authorizedInsurances && sortBy(authorizedInsurances, "planName");
      }
      catch {
        error = "Unable to load available accuracy insurance plans at this time.";
      }
    }

    this.setState((prevState): ProjectOptionsState => ({
      ...prevState,
      accuracyInsurancesIsLoading: false,
      authorizedInsurances: sortedAuthorizedInsurances,
      error: error,
      project:
      {
        ...prevState.project,
        accuracySpecialtyDefinitionId: null,
        accuracyTemplateCollectionId: null,
        accuracyTemplateCorrelationId: null,
      },
      useAccuracy: checked,
    }));
  }

  private handleUseAdequacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = !!e.target.checked;
    const freshMembershipOverrides = this.getFreshMembershipOverrides(this.state.project);
    //reset adequacy fields when check box changes
    this.setState((prevState): ProjectOptionsState => ({
      ...prevState,
      isZipProject: false,
      project:
      {
        ...prevState.project,
        accessCodeMapSetId: null,
        accuracySpecialtyDefinitionId: null,
        adequacyTemplateCollectionId: null,
        adequacyTemplateCorrelationId: null,
        displayOnNetworkAccordion: !checked ? null : prevState.displayOnNetworkAccordion,
        editProjectOverrideMemberSets: freshMembershipOverrides,
        excludedSpecialtyGroupCodes: [],
        marketProviderSetId: null,
        marketSpecialtyCodeMapSetId: null,
        memberSetAlias: null,
        memberSetAlias2: null,
        memberSetId: null,
        memberSetId2: null,
        networkProviderSetId: null,
        prospectProviderSetId: null,
        showAffiliationsInsights: !checked ? null : prevState.showAffiliationsInsights,
        showCareJourneyScores: !checked ? null : prevState.project?.showCareJourneyScores,
        showLocationConfidenceScores: !checked ? null : prevState.project?.showLocationConfidenceScores,
        showMotiveAppropriatenessScore: !checked ? null : prevState.project?.showMotiveAppropriatenessScore,
        showPciDashboard: !checked ? null : prevState.project?.showPciDashboard,
        showPciData: !checked ? null : prevState.project?.showPciData,
        showTricareClaimsVolumeColumn: !checked ? null : prevState.project?.showTricareClaimsVolumeColumn,
        showVaClaimsVolumeColumn: !checked ? null : prevState.project?.showVaClaimsVolumeColumn,
        telehealthSpecialtyGroupCodes: [],
        useMarketProvidersForImpact: null,
      },
      projectAccessCodeMapSets: [],
      projectMemberPopulations: [],
      specialtyCodeMapSets: [],
      //selectedSpecialtyGroupCodes: [],
      useAdequacy: checked,
    }));
    this.removeAlternateMemberships();
    this.generateSpecialtyOverrides(this.state.specialtyGroups);
  }

  private handleAuthorizedInsurancesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    //set state for currently selected insurance
    const uid = e.target.value;

    this.setState((prevState) => ({
      ...prevState,
      selectedInsuranceUid: uid,
    }));
  }

  private handleAddInspectedInsurance = () => {
    const uid = this.state.selectedInsuranceUid;
    const selectedInsurances = this.state.selectedInsurances;

    if (!uid) { return; }

    //get the insurance we want to add
    const insToAdd = this.state.authorizedInsurances.find((ins) => {
      return ins.uid === uid;
    });

    //check if insurance we want to add is already in list of selected insurances
    const existingIns = selectedInsurances.find((ins) => {
      return ins.uid === uid;
    });

    if (insToAdd && !existingIns) {
      selectedInsurances.push(insToAdd);

      const sortedSelectedInsurances = selectedInsurances && sortBy(selectedInsurances, "planName");

      this.setState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          inspectedInsurancesList: sortedSelectedInsurances,
        },
        selectedInsurances: sortedSelectedInsurances,
      }));
    }
  }

  private handleRemoveInspectedInsurance = (uid: string) => {
    const selectedInsurances = this.state.selectedInsurances;
    const index = selectedInsurances.findIndex((ins) => ins.uid === uid);

    if (uid && index > -1) {
      selectedInsurances.splice(index, 1);

      this.setState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          inspectedInsurancesList: selectedInsurances,
        },
        selectedInsurances,
      }));
    }
  }

  private toggleMemberBinding = () => {
    let tracker = false;
    const { project } = this.state;
    const overrides: EditProjectOverrideMemberSet[] = project?.editProjectOverrideMemberSets ?? [];
    overrides.forEach((x) => {
      if (x.projectOverrideMemberSetType === "Alternate" && x.memberSetId != null) {
        this.setState((prevState) => ({ ...prevState, disableMemberBindingTable: false }));
        tracker = true;
      }
    });
    if (tracker === false) {
      this.setState((prevState) => ({ ...prevState, disableMemberBindingTable: true }));
    }
  }

  private getActiveMembershipOverrides = (project: Project): EditProjectOverrideMemberSet[] => {
    const primary: EditProjectOverrideMemberSet = {
      alias: project.memberSetAlias,
      customProjectId: project.projectId,
      memberSetId: project.memberSetId,
      memberSetName: project.memberSetName,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Primary,
      secondarySpecialtyGroupCodes: [],
    };
    const secondary: EditProjectOverrideMemberSet = {
      alias: project.memberSetAlias2,
      customProjectId: project.projectId,
      memberSetId: project.memberSetId2,
      memberSetName: project.memberSetName2,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Secondary,
      secondarySpecialtyGroupCodes: [],
    };
    const risk: EditProjectOverrideMemberSet = {
      alias: project.membershipRiskMemberSetAlias,
      customProjectId: project.projectId,
      memberSetId: project.membershipRiskMemberSetId,
      memberSetName: "Risk Analysis Membership",
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Risk,
      secondarySpecialtyGroupCodes: [],
    };
    const editProjectOverrideMemberSets: EditProjectOverrideMemberSet[] = project?.editProjectOverrideMemberSets ?? [];
    if (this.props.user.roles.some((r) => r === Roles.MEMBERSHIP_RISK_USER)) { editProjectOverrideMemberSets.unshift(risk); }
    editProjectOverrideMemberSets.unshift(secondary);
    editProjectOverrideMemberSets.unshift(primary);

    return editProjectOverrideMemberSets;
  }

  private getFreshMembershipOverrides = (project: Project): EditProjectOverrideMemberSet[] => {
    const primary: EditProjectOverrideMemberSet = {
      alias: "",
      customProjectId: project.projectId,
      memberSetId: null,
      memberSetName: null,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Primary,
      secondarySpecialtyGroupCodes: [],
    };
    const secondary: EditProjectOverrideMemberSet = {
      alias: "",
      customProjectId: project.projectId,
      memberSetId: null,
      memberSetName: null,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Secondary,
      secondarySpecialtyGroupCodes: [],
    };
    const risk: EditProjectOverrideMemberSet = {
      alias: "",
      customProjectId: project.projectId,
      memberSetId: null,
      memberSetName: "Risk Analysis Membership",
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Risk,
      secondarySpecialtyGroupCodes: [],
    };
    const editProjectOverrideMemberSets: EditProjectOverrideMemberSet[] = [];
    editProjectOverrideMemberSets.push(primary);
    editProjectOverrideMemberSets.push(secondary);
    if (this.props.user.roles.some((s) => s === Roles.MEMBERSHIP_RISK_USER)) { editProjectOverrideMemberSets.push(risk); }

    return editProjectOverrideMemberSets;
  }

  private generateAltMemberships = () => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = project?.editProjectOverrideMemberSets ?? [];
    const { projectMemberPopulations } = this.state;
    let altMembers: MemberSetModel[] = [];
    let empty = true;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate && x.memberSetId != null) {
        empty = false;
        if (!!projectMemberPopulations) {
          projectMemberPopulations.forEach((y) => {
            if (x.memberSetId === y.memberSetId) {
              altMembers.push(y);
            }
          });
        }
      }
    });
    if (empty === true) {
      altMembers = this.state.projectMemberPopulations;
    }
    this.setState((prevState) => ({
      ...prevState,
      alternateMemberships: altMembers,
    }));
  }

  private removeNonAlternateMemberships = () => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = [];
    (project?.editProjectOverrideMemberSets ?? []).forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) {
        updates.push(x);
      }
    });
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
      },
    }));
  }

  private removeAlternateMemberships = () => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = [];
    (project?.editProjectOverrideMemberSets ?? []).forEach((x) => {
      if (x.projectOverrideMemberSetType !== ProjectOverrideMemberSetType.Alternate) {
        updates.push(x);
      }
    });
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
      },
    }));
  }

  private handlePrimaryMembershipBindingChange = (memberPop: number, code: string) => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project?.editProjectOverrideMemberSets ?? []);
    //Remove code (Specialty) from existing members, before adding code to the new member. This keeps the state in sync with UI.
    updates.forEach((x) => {
      x.primarySpecialtyGroupCodes.forEach((y) => {
        if (y === code) {
          x.primarySpecialtyGroupCodes.splice(x.primarySpecialtyGroupCodes.indexOf(y), 1);
        }
      });
    });

    if (!(memberPop === null)) {
      updates.forEach((x) => {
        if (x.memberSetId === memberPop) {
          x.primarySpecialtyGroupCodes.push(code);
        }
      });
    }
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
      },
    }));
  }

  private handleSecondaryMembershipBindingChange = (memberPop: number, code: string) => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project?.editProjectOverrideMemberSets ?? []);
    //Remove code (Specialty) from existing members, before adding code to the new member. This keeps the state in sync with UI.
    updates.forEach((x) => {
      x.secondarySpecialtyGroupCodes.forEach((y) => {
        if (y === code) {
          x.secondarySpecialtyGroupCodes.splice(x.secondarySpecialtyGroupCodes.indexOf(y), 1);
        }
      });
    });

    if (!(memberPop === null)) {
      updates.forEach((x) => {
        if (x.memberSetId === memberPop) {
          x.secondarySpecialtyGroupCodes.push(code);
        }
      });
    }
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
      },
    }));
  }

  private generateSpecialtyOverrides = (sortedRecords: TemplateSpecialtyGroup[]) => {
    const specialtyOverrides: SpecialtyOverrides[] = [];

    if (sortedRecords) {
      sortedRecords.forEach((x) => {
        specialtyOverrides.push({ code: x.code, primaryMembership: null, secondaryMembership: null });
      });

      specialtyOverrides.forEach((x) => {
        (this.state.project?.editProjectOverrideMemberSets ?? []).forEach((y) => {
          y.primarySpecialtyGroupCodes.forEach((z) => {
            if (z === x.code) {
              x.primaryMembership = y.memberSetId;
            }
          });
          y.secondarySpecialtyGroupCodes.forEach((z) => {
            if (z === x.code) {
              x.secondaryMembership = y.memberSetId;
            }
          });
        });
      });
    }

    this.setState((prevState) => ({
      ...prevState,
      specialtyOverrides,
    }));
  }

  private setPrimarySpecialtyOverride = (memberPop: number, code: string) => {
    const { specialtyOverrides } = this.state;
    specialtyOverrides.forEach((x) => {
      if (x.code === code) {
        x.primaryMembership = memberPop;
      }
    });
    this.setState((prevState) => ({
      ...prevState,
      specialtyOverrides,
    }));
  }

  private setSecondarySpecialtyOverride = (memberPop: number, code: string) => {
    const { specialtyOverrides } = this.state;
    specialtyOverrides.forEach((x) => {
      if (x.code === code) {
        x.secondaryMembership = memberPop;
      }
    });
    this.setState((prevState) => ({
      ...prevState,
      specialtyOverrides,
    }));
  }

  private handleTemplateCorrelationIdChange = async (templateCorrelationId: string, userRoles: string[]) => {
    if (templateCorrelationId === "") {
      this.setState((prevState): ProjectOptionsState => ({
        ...prevState,
        project: {
          ...prevState.project,
          adequacyTemplateCorrelationId: templateCorrelationId,
          excludedSpecialtyGroupCodes: [],
          timeDistanceCalculatorType: TimeDistanceCalculatorType.Unknown,
        },
        projectAccessCodeMapSets: [],
        projectMemberPopulations: [],
        sortedSpecialtyGroups: [],
      }));
    } else {
      const templateInfo =
        await this.templateHelper.getTemplateInfo(
          this.initialProjectState,
          this.state.allAccessCodeMapSets,
          this.state.allMemberPopulations,
          this.state.templateCollections,
          this.state.project?.adequacyTemplateCollectionId,
          templateCorrelationId,
          this.state.project?.templateId);

      const isZipTemplate = this.state.templateCollections.find((tc) =>
        tc.templateCollectionId === this.state.project?.adequacyTemplateCollectionId)
        .resource?.resourceType === ResourceType.AdequacyZipTemplateCollection;

      const sortedSpecialtyGroups = templateInfo.specialtyGroups && sortBy(templateInfo.specialtyGroups, this.sortOrder);
      const hasCmsApplicationCredits: boolean = sortedSpecialtyGroups.some(specialtyGroup => specialtyGroup.hasCMSApplicationCreditPercentages);

      const showMotiveAppropriatenessScore = templateInfo.motiveCrosswalkCodeMapSetId !== undefined && templateInfo.motiveCrosswalkCodeMapSetId !== null;
      const thingsToReset: Partial<ProjectOptionsState> = cloneThingsToResetIfZipProject(isZipTemplate) as Partial<ProjectOptionsState>;
      const projectThingsToReset: Partial<Project> = cloneProjectThingsToResetIfZipProject(isZipTemplate);

      this.setState((prevState): ProjectOptionsState => ({
        ...prevState,
        hideCMSApplicationCreditOption: !hasCmsApplicationCredits,
        hideMotiveAppropriatenessScore: !showMotiveAppropriatenessScore,
        isZipProject: isZipTemplate,
        project: {
          ...prevState.project,
          adequacyTemplateCorrelationId: templateCorrelationId,
          excludeInVolumeActions: isZipTemplate ? true : prevState.project?.excludeInVolumeActions,
          excludedSpecialtyGroupCodes: [],
          showCMSCredit: !hasCmsApplicationCredits ? false : this.state.project?.showCMSCredit,
          showMotiveAppropriatenessScore: !showMotiveAppropriatenessScore ? false : this.state.project?.showMotiveAppropriatenessScore,
          strictTimeAndDistance: isZipTemplate ? true : prevState.project?.strictTimeAndDistance,
          timeDistanceCalculatorType: this.templateHelper.getTimeDistanceCalculatorType(templateInfo, userRoles, prevState.project?.timeDistanceCalculatorType),
          ...projectThingsToReset,
        },
        projectAccessCodeMapSets: templateInfo.projectAccessCodeMapSets,
        projectMemberPopulations: templateInfo.projectMemberPopulations,
        sortedSpecialtyGroups,
        templateAccessGranularity: templateInfo.templateAccessGranularity,
        timeDistanceCalculatorTypes: templateInfo.timeDistanceCalculatorTypes,
        ...thingsToReset,
      }));
    }
  }

  private handleTimeDistanceCalculatorTypeChange = (timeDistanceCalculatorType: TimeDistanceCalculatorType) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        timeDistanceCalculatorType,
      },
    }));
  }

  private toggleShowClosestProviderReportOption = () => {
    const { project } = this.state;
    const currentMemberPops = this.state.allMemberPopulations?.filter(function (pop) {
      return (pop.memberSetId == project.memberSetId || pop.memberSetId == project.memberSetId2 ||
        (project?.editProjectOverrideMemberSets ?? []).some(function (overrideMemberSet) { return pop.memberSetId == overrideMemberSet.memberSetId }))
    })

    const anyCensus = currentMemberPops?.some(function (pop) {
      return pop.memberSetType == MemberSetTypeEnum.Census;
    })
    let hideClosestProviderReportOption = false;
    let showClosestProviderReport = this.initialProjectState?.project?.showClosestProviderReport;
    if (anyCensus) {
      hideClosestProviderReportOption = true;
      showClosestProviderReport = false;
    }

    this.setState((prevState) => ({
      ...prevState,
      hideClosestProviderReportOption: hideClosestProviderReportOption,
      project: {
        ...prevState.project,
        showClosestProviderReport: showClosestProviderReport,
      },
    }));
  }

  private handleMemberPopChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberPop = +(e.target.value);

    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project?.editProjectOverrideMemberSets ?? []);
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary) {
        x.memberSetId = memberPop;
      }
    });

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
        memberSetId: memberPop ? memberPop : null,
      },
    }), this.toggleShowClosestProviderReportOption);
  }

  private handleMemberPopChange2 = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberPop = +(e.target.value);

    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project?.editProjectOverrideMemberSets ?? []);
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary) {
        x.memberSetId = memberPop;
        x.alias = memberPop ? x.alias : "";
      }
    });

    let alias = "";
    if (memberPop) {
      alias = project.memberSetAlias2;
    }

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
        memberSetAlias2: alias,
        memberSetId2: memberPop ? memberPop : null,
      },
    }), this.toggleShowClosestProviderReportOption);
  }

  private handleMemberPopAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project?.editProjectOverrideMemberSets ?? []);
    const memberSetAlias = e.target.value;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === "Primary") {
        x.alias = e.target.value;
      }
    });

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        memberSetAlias,
      },
    }));
  }

  private handleMemberPopAliasChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project?.editProjectOverrideMemberSets ?? []);
    const memberSetAlias2 = e.target.value;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === "Secondary") {
        x.alias = e.target.value;
      }
    });

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
        memberSetAlias2,
      },
    }));
  }

  private handleAlternateMemberPopAliasChange = (rows: EditProjectOverrideMemberSet[]) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: rows,
      },
    }));
  }

  private handleAlternateMemberPopChange = (memberPopId: number, prevMemberPopId: number) => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project?.editProjectOverrideMemberSets ?? []);
    const memberName = this.state.allMemberPopulations.filter((ind) => ind.memberSetId === memberPopId)[0]?.resource?.name;
    if (prevMemberPopId === null) {
      for (const x of updates) {
        if (x.memberSetId === null && x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) {
          x.memberSetId = memberPopId;
          x.memberSetName = memberName;
          break;
        }
      }
    } else {
      updates.forEach((x) => {
        if (x.memberSetId === prevMemberPopId && x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) {
          x.memberSetId = memberPopId;
          x.memberSetName = memberName;
          x.primarySpecialtyGroupCodes.forEach((y) => {
            this.setPrimarySpecialtyOverride(memberPopId, y);
          });
          x.secondarySpecialtyGroupCodes.forEach((y) => {
            this.setSecondarySpecialtyOverride(memberPopId, y);
          });
        }
      });
    }

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        createProjectOverrideMemberSets: updates,
      },
    }), this.toggleShowClosestProviderReportOption);

    this.toggleMemberBinding();
  }

  private handleNewAlternativeMemberPop = () => {
    const newAltMember: EditProjectOverrideMemberSet = {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: "",
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Alternate,
      secondarySpecialtyGroupCodes: [],
    };

    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project?.editProjectOverrideMemberSets ?? []);
    updates.push(newAltMember);
    this.toggleMemberBinding();

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        createProjectOverrideMemberSets: updates,
      },
    }));
  }

  private handleDeleteAlternateRow = (rows: EditProjectOverrideMemberSet[]) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: rows,
      },
    }), this.toggleShowClosestProviderReportOption);
    this.generateAltMemberships();
    this.toggleMemberBinding();
  }

  private handleAccessCodeMapSetChange = (accessCodeMapSetId: number) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        accessCodeMapSetId: accessCodeMapSetId ? accessCodeMapSetId : null,
      },
    }));
  }

  private handleNetworkTaxonomyChange = (networkTaxonomyData: NetworkTaxonomyData, isValid: boolean) => {
    this.setState({
      isNetworkTaxonomyValid: isValid,

      project: {
        ...this.state.project,
        networkTaxonomy: networkTaxonomyData,
      },
    })
  }

  /** Sets the active pane to Composite. */
  private handleDetailsPaneClick = () => {
    this.setState({
      activeEditModule: "Details",
    });
  }

  /** Sets the active pane to Accuracy. */
  private handleTemplatePaneClick = () => {
    this.setState({
      activeEditModule: "Template",
    });
  }

  /** Sets the active pane to Adequacy. */
  private handleMembersPaneClick = () => {
    this.setState({
      activeEditModule: "Members",
    });
  }

  private handleNetworkTaxonomyPaneClick = () => {
    this.setState({
      activeEditModule: "Network Taxonomy"
    })
  }

  /** Sets the active pane to Adequacy. */
  private handleProvidersPaneClick = () => {
    this.setState({
      activeEditModule: "Providers",
    });
  }

  /** Sets the active pane to Adequacy. */
  private handleOptionsPaneClick = () => {
    this.setState({
      activeEditModule: "Options",
    });
  }

  private isEmpty = (value?: string) => !value || value.trim().length === 0;

  private isSubmitDisabled = () => {
    //validation independent of adequacy or accuracy
    if (this.state.project?.isRunning ||
      this.state.project?.isReadOnly ||
      this.state.project?.resource?.name === "" ||
      this.state.project?.tags?.some(tag => !this.isEmpty(tag.tagGroupName) && this.isEmpty(tag.tagName)) ||
      this.state.project?.tags?.some(tag => this.isEmpty(tag.tagGroupName) && !this.isEmpty(tag.tagName)) ||
      !this.state.project?.networkProviderSetId ||
      this.state.project?.networkProviderSetId === 0 ||
      !this.state.isNetworkTaxonomyValid) {
      return true;
    }

    //user must select at least adequacy or accuracy
    if (!this.state.project?.accuracyTemplateCollectionId &&
      !this.state.project?.adequacyTemplateCollectionId) {
      return true;
    }

    //user selected Accuracy template collection but no accuracy template
    if (this.state.project?.accuracyTemplateCollectionId &&
      !this.state.project?.accuracyTemplateCorrelationId) {
      return true;
    }

    //if accuracy-only project, ensure specialty definition is selected
    if (this.state.project?.accuracyTemplateCollectionId &&
      !this.state.project?.adequacyTemplateCollectionId &&
      !this.state.project?.accuracySpecialtyDefinitionId &&
      (!this.state.sortedSpecialtyGroups ||
        !this.state.project?.excludedSpecialtyGroupCodes ||
        this.state.sortedSpecialtyGroups.length === this.state.project?.excludedSpecialtyGroupCodes.length)) {
      return true;
    }

    //user selected adequacy template collection but no adequacy template
    if (this.state.project?.adequacyTemplateCollectionId &&
      !this.state.project?.adequacyTemplateCorrelationId) {
      return true;
    }

    const memberSetsWithAliases = this.state.project.editProjectOverrideMemberSets.filter((oms) => !!oms.alias);
    const memberSetAliases = new Set(memberSetsWithAliases.map((oms) => oms.alias));
    //if adequacy is selected, validate the rest of its fields
    if (this.state.project?.adequacyTemplateCollectionId &&
      ((this.state.projectAccessCodeMapSets &&
        this.state.projectAccessCodeMapSets.length > 0 &&
        !this.state.project?.accessCodeMapSetId) ||
        !this.state.project?.memberSetId ||
        !this.state.sortedSpecialtyGroups ||
        this.state.sortedSpecialtyGroups.length === this.state.project?.excludedSpecialtyGroupCodes.length ||
        this.state.project?.resource?.description === "" ||
        !this.state.project?.timeDistanceCalculatorType || this.state.project?.timeDistanceCalculatorType === undefined ||
        memberSetsWithAliases.length !== memberSetAliases.size)
    ) {
      return true;
    }

    return false;
  }

  private getAccuracySpecialtyGroups = async (specialtyDefinitionId: string) => {
    const specialtyDefinition = await this.accuracyTemplateService
      .getSpecialtyDefinitionById(specialtyDefinitionId);

    const specialtyGroups: TemplateSpecialtyGroup[] = [];

    for (const category of specialtyDefinition.categories) {
      for (const specialtyGroup of category.specialtyGroups) {
        const templateSpecialtyGroup: TemplateSpecialtyGroup = {
          code: specialtyGroup.code,
          displayOrder: specialtyGroup.displayOrder,
          //hasJoinedAdequacyServicingCriteria: null,
          name: specialtyGroup.name,
          servicingCountBy: null,
          servicingMinimumType: null,
        };

        specialtyGroups.push(templateSpecialtyGroup);
      }
    }
    return specialtyGroups && sortBy(specialtyGroups, this.sortOrder);
  }

  private isDirty = () => {
    if (!this.initialProjectState) {
      //page hasn't had time to load yet or it's a shared project
      return false;
    }
    const project = this.state.project;
    if (project && (project.resource?.isShared || project.isRunning || project.isReadOnly)) {
      return false;
    }

    const excludeDeactivatedFacilityImpact = this.state.excludeDeactivatedFacilityImpact;
    const excludeDeactivatedFacilityNetwork = this.state.excludeDeactivatedFacilityNetwork;
    const excludeDeactivatedProviderImpact = this.state.excludeDeactivatedProviderImpact;
    const excludeDeactivatedProviderNetwork = this.state.excludeDeactivatedProviderNetwork;
    const excludeInvalidNppesNpiFacilityImpact = this.state.excludeInvalidNppesNpiFacilityImpact;
    const excludeInvalidNppesNpiFacilityNetwork = this.state.excludeInvalidNppesNpiFacilityNetwork;
    const excludeInvalidNppesNpiProviderImpact = this.state.excludeInvalidNppesNpiProviderImpact;
    const excludeInvalidNppesNpiProviderNetwork = this.state.excludeInvalidNppesNpiProviderNetwork;
    const excludeMissingNpiFacilityImpact = this.state.excludeMissingNpiFacilityImpact;
    const excludeMissingNpiFacilityNetwork = this.state.excludeMissingNpiFacilityNetwork;
    const excludeMissingNpiProviderImpact = this.state.excludeMissingNpiProviderImpact;
    const excludeMissingNpiProviderNetwork = this.state.excludeMissingNpiProviderNetwork;
    const excludeMedicareOptOutNetwork = this.state.excludeMedicareOptOutNetwork;
    const excludeOIGExclusionNetwork = this.state.excludeOIGExclusionNetwork;
    const excludePrecludedNetwork = this.state.excludePrecludedNetwork;
    const excludeMedicareOptOutImpact = this.state.excludeMedicareOptOutImpact;
    const excludeOIGExclusionImpact = this.state.excludeOIGExclusionImpact;
    const excludePrecludedImpact = this.state.excludePrecludedImpact;
    const showAffiliationsInsights = this.state.showAffiliationsInsights;
    const displayOnNetworkAccordion = this.state.displayOnNetworkAccordion;
    const timeDistanceCalculatorTypes = this.state.timeDistanceCalculatorTypes;

    const currentProjectState = {
      displayOnNetworkAccordion,
      excludeDeactivatedFacilityImpact,
      excludeDeactivatedFacilityNetwork,
      excludeDeactivatedProviderImpact,
      excludeDeactivatedProviderNetwork,
      excludeInvalidNppesNpiFacilityImpact,
      excludeInvalidNppesNpiFacilityNetwork,
      excludeInvalidNppesNpiProviderImpact,
      excludeInvalidNppesNpiProviderNetwork,
      excludeMedicareOptOutImpact,
      excludeMedicareOptOutNetwork,
      excludeMissingNpiFacilityImpact,
      excludeMissingNpiFacilityNetwork,
      excludeMissingNpiProviderImpact,
      excludeMissingNpiProviderNetwork,
      excludeOIGExclusionImpact,
      excludeOIGExclusionNetwork,
      excludePrecludedImpact,
      excludePrecludedNetwork,
      project,
      showAffiliationsInsights,
      timeDistanceCalculatorTypes
    };

    return !isEqual(currentProjectState, this.initialProjectState);
  }

  private handleFormSubmit = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    this.setState({ submitClicked: true });

    await this.exclusionsToSingleArray();
    try {
      this.removeNonAlternateMemberships();
      const project: Project = structuredClone(this.state.project);
      if (!project.marketProviderSetId && project.useMarketProvidersForImpact) {
        project.useMarketProvidersForImpact = false;
      }
      const updatedProject = await this.projectService.update(project);
      const successfulUpdate = !!updatedProject;
      if (successfulUpdate && this.props.onSubmitSuccess) {
        this.props.onSubmitSuccess().then(() => this.props.setIsFlyoutVisible(false));
      }
    } catch (error) {
      this.setState({
        error: "A project with that name already exists.",
        submitClicked: false,
      });
    }
  }

  private exclusionsToSingleArray = async () => {
    const updatedNetworkExclusions: ProjectExclusionType[] = [];

    if (this.state.excludeDeactivatedFacilityNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.DeactivatedFacility);
    }

    if (this.state.excludeDeactivatedProviderNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.DeactivatedProvider);
    }

    if (this.state.excludeInvalidNppesNpiFacilityNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.InvalidNppesNpiFacility);
    }

    if (this.state.excludeInvalidNppesNpiProviderNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.InvalidNppesNpiProvider);
    }

    if (this.state.excludeMissingNpiFacilityNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.MissingNpiFacility);
    }

    if (this.state.excludeMissingNpiProviderNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.MissingNpiProvider);
    }

    if (this.state.excludeMedicareOptOutNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.MedicareOptOut);
    }

    if (this.state.excludeOIGExclusionNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.OIGExclusion);
    }

    if (this.state.excludePrecludedNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.Precluded);
    }

    if (updatedNetworkExclusions.length === 0) {
      updatedNetworkExclusions.push(ProjectExclusionType.None);
    }

    const updatedImpactExclusions: ProjectExclusionType[] = [];

    if (this.state.excludeDeactivatedFacilityImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.DeactivatedFacility);
    }

    if (this.state.excludeDeactivatedProviderImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.DeactivatedProvider);
    }

    if (this.state.excludeInvalidNppesNpiFacilityImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.InvalidNppesNpiFacility);
    }

    if (this.state.excludeInvalidNppesNpiProviderImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.InvalidNppesNpiProvider);
    }

    if (this.state.excludeMissingNpiFacilityImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.MissingNpiFacility);
    }

    if (this.state.excludeMissingNpiProviderImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.MissingNpiProvider);
    }

    if (this.state.excludeMedicareOptOutImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.MedicareOptOut);
    }

    if (this.state.excludeOIGExclusionImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.OIGExclusion);
    }

    if (this.state.excludePrecludedImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.Precluded);
    }

    if (updatedImpactExclusions.length === 0) {
      updatedImpactExclusions.push(ProjectExclusionType.None);
    }

    const project = {
      ...this.state.project,
      impactExclusions: updatedImpactExclusions,
      networkExclusions: updatedNetworkExclusions,
    };

    this.setState({ project });
  }

  private IsProjectUsingZipTemplate(templateCollections: TemplateCollectionMetadata[], project: Project): boolean {
    if (project.adequacyTemplateCollectionId === undefined)
      return false;
    const matchedAdequacyTemplateCollection = templateCollections.find((tc: TemplateCollectionMetadata) => tc.templateCollectionId === project.adequacyTemplateCollectionId);
    return matchedAdequacyTemplateCollection.resource?.resourceType === ResourceType.AdequacyZipTemplateCollection;
  }
}

const WrappedEditProjectOptions = withRouter(withFlyoutContext(withUserContext(withFeatureFlagContext(EditProject))));

export { WrappedEditProjectOptions as EditProject };
